/**
 * STATUS PROGRESS BAR
 *
 * Horizontal stacked bar showing status distribution in compact form.
 * Used in tables and lists where a full pie chart would be too large.
 *
 * Each segment is proportionally sized based on status counts.
 */

import React, { useMemo } from 'react'
import { token } from '@atlaskit/tokens'
import Tooltip from '@atlaskit/tooltip'

// Status color mapping using Atlaskit design tokens
// Uses color.icon.* tokens to match ExecutionCell icon colors
const STATUS_COLORS = {
  'Pass': token('color.icon.success', '#22A06B'),
  'Passed': token('color.icon.success', '#22A06B'),
  'Fail': token('color.icon.danger', '#DE350B'),
  'Failed': token('color.icon.danger', '#DE350B'),
  'Blocked': token('color.icon.warning', '#FF991F'),
  'Block': token('color.icon.warning', '#FF991F'),
  'In Progress': token('color.icon.information', '#0065FF'),
  'Not Run': token('color.background.neutral', '#DFE1E6'),
  'Skip': token('color.icon.subtle', '#6B778C'),
  'Skipped': token('color.icon.subtle', '#6B778C'),
  'Query': token('color.icon.information', '#0065FF'),
  'Exclude': token('color.icon.subtle', '#6B778C'),
  'Excluded': token('color.icon.subtle', '#6B778C')
}

// Accent colors for dynamic/custom statuses
const ACCENT_COLORS = [
  '#6554C0', '#00B8D9', '#FF5630', '#36B37E',
  '#FFAB00', '#403294', '#00A3BF', '#BF2600'
]

/**
 * Get color for a status name
 *
 * @param {string} statusName - Name of the status
 * @param {number} index - Index for accent color selection
 * @returns {string} Color value
 */
const getStatusColor = (statusName, index) => {
  if (STATUS_COLORS[statusName]) {
    return STATUS_COLORS[statusName]
  }

  const normalizedName = statusName.toLowerCase()
  for (const [key, color] of Object.entries(STATUS_COLORS)) {
    if (key.toLowerCase() === normalizedName) {
      return color
    }
  }

  return ACCENT_COLORS[index % ACCENT_COLORS.length]
}

/**
 * StatusProgressBar Component
 *
 * @param {object} props
 * @param {object} props.statusDistribution - Map of status name to count
 * @param {number} props.height - Bar height in pixels (default: 8)
 * @param {string} props.width - Bar width (default: '100%')
 * @param {boolean} props.showTooltip - Whether to show tooltip on hover (default: true)
 * @param {number} props.borderRadius - Border radius in pixels (default: 4)
 */
const StatusProgressBar = ({
  statusDistribution = {},
  height = 8,
  width = '100%',
  showTooltip = true,
  borderRadius = 4
}) => {
  // Transform and calculate segments
  const segments = useMemo(() => {
    const entries = Object.entries(statusDistribution)
    const total = entries.reduce((sum, [, count]) => sum + count, 0)

    if (total === 0) {
      return []
    }

    return entries
      .filter(([, count]) => count > 0)
      .map(([name, value], index) => ({
        name,
        value,
        percentage: (value / total) * 100,
        color: getStatusColor(name, index)
      }))
      // Sort by predefined order: Pass, Fail, Blocked, then others
      .sort((a, b) => {
        const order = ['Pass', 'Passed', 'Fail', 'Failed', 'Blocked', 'Block', 'Query', 'Exclude', 'Excluded', 'Skip', 'Skipped', 'In Progress', 'Not Run']
        const aIndex = order.findIndex(s => s.toLowerCase() === a.name.toLowerCase())
        const bIndex = order.findIndex(s => s.toLowerCase() === b.name.toLowerCase())

        if (aIndex === -1 && bIndex === -1) return 0
        if (aIndex === -1) return 1
        if (bIndex === -1) return -1
        return aIndex - bIndex
      })
  }, [statusDistribution])

  // Calculate total
  const total = useMemo(() => {
    return segments.reduce((sum, item) => sum + item.value, 0)
  }, [segments])

  // Build tooltip content
  const tooltipContent = useMemo(() => {
    if (segments.length === 0) {
      return 'No data'
    }

    return (
      <div style={{ padding: '4px 0' }}>
        {segments.map((segment, index) => (
          <div
            key={index}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: index < segments.length - 1 ? '4px' : 0
            }}
          >
            <div
              style={{
                width: '10px',
                height: '10px',
                borderRadius: '2px',
                backgroundColor: segment.color,
                flexShrink: 0
              }}
            />
            <span>{segment.name}:</span>
            <span style={{ fontWeight: 500 }}>
              {segment.value} ({segment.percentage.toFixed(1)}%)
            </span>
          </div>
        ))}
        <div style={{
          marginTop: '8px',
          paddingTop: '4px',
          borderTop: '1px solid rgba(255,255,255,0.2)',
          fontWeight: 500
        }}>
          Total: {total}
        </div>
      </div>
    )
  }, [segments, total])

  // Handle empty state
  if (segments.length === 0) {
    return (
      <div
        style={{
          width,
          height,
          backgroundColor: token('color.background.neutral', '#DFE1E6'),
          borderRadius: borderRadius
        }}
      />
    )
  }

  // Render bar
  const bar = (
    <div
      style={{
        width,
        height,
        backgroundColor: token('color.background.neutral.subtle', '#F4F5F7'),
        borderRadius: borderRadius,
        overflow: 'hidden',
        display: 'flex'
      }}
    >
      {segments.map((segment, index) => (
        <div
          key={index}
          style={{
            width: `${segment.percentage}%`,
            height: '100%',
            backgroundColor: segment.color,
            // First and last segments get rounded corners
            borderTopLeftRadius: index === 0 ? borderRadius : 0,
            borderBottomLeftRadius: index === 0 ? borderRadius : 0,
            borderTopRightRadius: index === segments.length - 1 ? borderRadius : 0,
            borderBottomRightRadius: index === segments.length - 1 ? borderRadius : 0
          }}
        />
      ))}
    </div>
  )

  // Wrap with tooltip if enabled
  if (showTooltip) {
    return (
      <Tooltip content={tooltipContent}>
        {bar}
      </Tooltip>
    )
  }

  return bar
}

export default StatusProgressBar
